import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Flask,
  Certificate,
  Barcode,
  Atom,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/375d05d8-af3a-4401-9df4-b71dc4d9d800/images/860c6bb206321e272a59cada0fbd2a1e6dd0e874be1d24a54181ccc895e36cd6.png";
const LAB_IMG =
  "https://static.prod-images.emergentagent.com/jobs/375d05d8-af3a-4401-9df4-b71dc4d9d800/images/5652539c588fad38dfb41d98d318338ee8df24a280cfdfc50ae08ec2c6fcd63a.png";

const CATEGORIES = [
  { name: "Healing & Recovery", icon: Flask },
  { name: "Metabolic", icon: Atom },
  { name: "Longevity", icon: ShieldCheck },
];

const ASSURANCES = [
  { icon: ShieldCheck, title: ">99% Purity", text: "HPLC & mass-spec verified on every synthesized batch." },
  { icon: Certificate, title: "COA Included", text: "Third-party Certificate of Analysis for full traceability." },
  { icon: Barcode, title: "Batch Traceability", text: "Every batch carries a unique number linked to its Certificate of Analysis for full traceability." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then(({ data }) => setFeatured(data.slice(0, 8)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-cyan/15">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-navy/30" />
        <div className="grid-texture absolute inset-0 opacity-60" />

        <div className="relative mx-auto flex max-w-7xl flex-col gap-8 px-6 py-24 sm:px-8 lg:flex-row lg:items-center lg:px-12 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 border border-cyan/30 bg-navy-surface/70 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan backdrop-blur">
              <Atom size={14} weight="fill" /> Laboratory-Grade Research Compounds
            </span>
            <h1 className="mt-6 font-heading text-4xl font-black leading-[1.05] tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Verified Quality.{" "}
              <span className="block text-cyan text-glow">Reliable Results.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-teal-muted sm:text-lg">
              Premium research compounds synthesized to laboratory-grade standards and validated by
              independent third-party labs. Every batch is HPLC- and mass-spec-verified for &gt;99% purity
              and ships with a batch-specific Certificate of Analysis, giving you complete traceability and
              reproducible results you can trust.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="group flex items-center gap-2 bg-cyan px-7 py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-navy-deep transition-all duration-300 hover:bg-cyan-bright hover:shadow-glow"
              >
                Browse Catalog
                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/#assurance"
                data-testid="hero-learn-btn"
                className="flex items-center gap-2 border border-cyan/40 px-7 py-3.5 font-mono text-sm font-semibold uppercase tracking-wider text-cyan transition-all duration-300 hover:bg-cyan/10"
              >
                Quality Standards
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mx-auto hidden w-full max-w-sm lg:block"
          >
            <div className="flex items-center justify-center border border-cyan/20 bg-navy-surface/50 p-10 shadow-glow-sm backdrop-blur">
              <img
                src="/quadhelix-mark.png"
                alt="QuadHelix Labs"
                className="w-full max-w-[280px] object-contain"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ASSURANCE */}
      <section id="assurance" className="border-b border-cyan/10 bg-navy-deep">
        <div className="mx-auto grid max-w-7xl gap-px bg-cyan/10 sm:grid-cols-3">
          {ASSURANCES.map((a) => (
            <div key={a.title} className="bg-navy-deep p-8 sm:p-10">
              <a.icon size={32} weight="duotone" className="text-cyan" />
              <h3 className="mt-4 font-heading text-xl font-bold text-white">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-teal-muted">{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">
              // Featured Compounds
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Research bestsellers
            </h2>
          </div>
          <Link
            to="/shop"
            data-testid="view-all-products"
            className="group flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-cyan hover:text-cyan-bright"
          >
            View full catalog
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-y border-cyan/10 bg-navy-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-12">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">
            // Research Areas
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
            Explore by category
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORIES.map((c) => (
              <Link
                key={c.name}
                to={`/shop?category=${encodeURIComponent(c.name)}`}
                data-testid={`category-${c.name}`}
                className="group flex flex-col gap-4 border border-cyan/15 bg-navy p-7 transition-all duration-300 hover:border-cyan/40 hover:shadow-glow-sm"
              >
                <c.icon size={30} className="text-cyan" weight="duotone" />
                <span className="font-heading text-lg font-bold text-white transition-colors group-hover:text-cyan">
                  {c.name}
                </span>
                <span className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-teal-muted">
                  Browse <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-cyan">
              // About QuadHelix
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reproducibility starts with purity
            </h2>
            <p className="mt-5 text-base leading-relaxed text-teal-muted">
              Every QuadHelix compound is synthesized in controlled environments and validated
              by independent laboratories. We publish a Certificate of Analysis for each batch,
              so your data is built on a foundation you can verify.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Independent third-party HPLC & MS testing",
                "Lyophilized for stability and long shelf life",
                "Full batch traceability via downloadable COA",
                "Strictly intended for in-vitro laboratory research",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-white/90">
                  <ShieldCheck size={18} weight="fill" className="mt-0.5 shrink-0 text-cyan" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative border border-cyan/20 p-2"
          >
            <img src={LAB_IMG} alt="QuadHelix research laboratory" className="w-full object-cover" />
            <div className="absolute inset-2 bg-gradient-to-t from-navy/60 to-transparent" />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
